import React, { useEffect, useState, useCallback, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Gamepad2, Loader2 } from 'lucide-react'
import { useTranslation } from '../hooks/use-i18n'
import matchService, { PlayedMatch } from '../services/matchService'
import { formatDate, getMatchDate } from '../utils/dateHelper'
import MatchTeamsInline from './MatchTeamsInline'
import { footballDisplayScore, footballLiveBadge, FOOTBALL_LIVE_POLL_MS, isFootballMatchLive } from '../utils/footballLiveScore'

function isMatchStarted(match: { date: string; time?: string }): boolean {
  const t = new Date(getMatchDate(match, true)).getTime()
  return !Number.isNaN(t) && Date.now() >= t
}

/** Normalize room match or played match to PlayedMatch shape */
function toPlayedMatch(m: { _id: string; id?: string; date: string | Date; time?: string; teamA: string; teamB: string; score?: string | null; winner?: string | null; league?: string; [key: string]: any }): PlayedMatch {
  const dateRaw = m.date instanceof Date ? m.date.toISOString().split('T')[0] : String(m.date).split('T')[0]
  return {
    _id: m._id ?? (m as any).id,
    id: (m as any).id ?? m._id,
    date: dateRaw,
    time: m.time ?? '00:00:00',
    teamA: m.teamA,
    teamB: m.teamB,
    score: m.score ?? '',
    winner: m.winner ?? null,
    league: m.league ?? '',
    playoff: (m as any).playoff ?? false,
    firstLegMatchScore: (m as any).firstLegMatchScore ?? null,
    winnerAdditionalTime: (m as any).winnerAdditionalTime ?? null,
    winnerPenalties: (m as any).winnerPenalties ?? null,
    liveScore: (m as any).liveScore ?? null,
    liveStatus: (m as any).liveStatus ?? null,
  }
}

const PAGE_SIZE = 50

type ListMode = 'all' | 'played' | 'future'

function getListMode(playedOnly: boolean, futureOnly: boolean): ListMode {
  if (playedOnly) return 'played'
  if (futureOnly) return 'future'
  return 'all'
}

function filterByListMode(matches: PlayedMatch[], mode: ListMode, trustServerFilter: boolean): PlayedMatch[] {
  if (trustServerFilter) return matches
  if (mode === 'played') return matches.filter(isMatchStarted)
  if (mode === 'future') return matches.filter((m) => !isMatchStarted(m))
  return matches
}

interface GamesAndPredictionsModalProps {
  roomId: string
  isOpen: boolean
  onClose: () => void
  onSelectGame: (match: PlayedMatch) => void
  /** When true, only matches whose kickoff time has passed are shown */
  playedOnly?: boolean
  /** When true, only matches whose kickoff is still in the future are shown */
  futureOnly?: boolean
  /** i18n key for modal title; defaults to roomPage.gamesAndPredictions */
  titleKey?: string
  /** When set, uses static matches instead of API (showcase demo) */
  demoMatches?: PlayedMatch[]
}

const sortByDate = (a: PlayedMatch, b: PlayedMatch, mode: ListMode) => {
  const ta = new Date(getMatchDate(a, true)).getTime()
  const tb = new Date(getMatchDate(b, true)).getTime()
  if (mode === 'played') {
    const liveA = isFootballMatchLive(a)
    const liveB = isFootballMatchLive(b)
    if (liveA !== liveB) return liveA ? -1 : 1
    return tb - ta
  }
  if (mode === 'future') {
    return ta - tb
  }
  const now = Date.now()
  const aFuture = ta > now
  const bFuture = tb > now
  if (aFuture !== bFuture) return aFuture ? -1 : 1
  if (aFuture) return ta - tb
  return tb - ta
}

const GamesAndPredictionsModal: React.FC<GamesAndPredictionsModalProps> = ({
  roomId,
  isOpen,
  onClose,
  onSelectGame,
  playedOnly = false,
  futureOnly = false,
  titleKey = 'roomPage.gamesAndPredictions',
  demoMatches,
}) => {
  const { t } = useTranslation()
  const listMode = getListMode(playedOnly, futureOnly)
  const trustServerFilter = playedOnly || futureOnly
  const isDemo = Boolean(demoMatches?.length)
  const [extraGames, setExtraGames] = useState<PlayedMatch[]>([])
  const [hasMoreExtra, setHasMoreExtra] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)

  const { data: firstPageData, isLoading, isError, refetch, isFetching } = useQuery({
    queryKey: ['room-played-matches', roomId, listMode],
    queryFn: async () => {
      const playedMatches = await matchService.getRoomPlayedMatches(roomId, PAGE_SIZE, 0, {
        playedOnly,
        futureOnly,
      })
      const next = filterByListMode(playedMatches.map((m) => toPlayedMatch(m)), listMode, trustServerFilter)
      next.sort((a, b) => sortByDate(a, b, listMode))
      return { list: next, hasMore: playedMatches.length === PAGE_SIZE }
    },
    enabled: isOpen && Boolean(roomId) && !isDemo,
    staleTime: 30 * 1000,
    refetchInterval: isOpen ? FOOTBALL_LIVE_POLL_MS : false,
    retry: 1,
  })

  const loading = isLoading && !firstPageData && !isDemo

  useEffect(() => {
    if (isOpen) {
      setExtraGames([])
      setHasMoreExtra(false)
    }
  }, [isOpen])

  const firstPage = isDemo
    ? filterByListMode(demoMatches ?? [], listMode, false).sort((a, b) => sortByDate(a, b, listMode))
    : (firstPageData?.list ?? [])
  const hasMoreFirst = isDemo ? false : (firstPageData?.hasMore ?? false)
  const totalLoaded = firstPage.length + extraGames.length
  const hasMore = hasMoreFirst || hasMoreExtra

  const games = useMemo(() => {
    const combined = [...firstPage, ...extraGames]
    const byId = new Map<string, PlayedMatch>()
    combined.forEach((g) => byId.set(g._id, g))
    return filterByListMode(Array.from(byId.values()), listMode, trustServerFilter).sort((a, b) => sortByDate(a, b, listMode))
  }, [firstPage, extraGames, listMode, trustServerFilter])

  const loadMore = useCallback(async () => {
    if (!roomId || isDemo) return
    setLoadingMore(true)
    try {
      const playedMatches = await matchService.getRoomPlayedMatches(roomId, PAGE_SIZE, totalLoaded, {
        playedOnly,
        futureOnly,
      })
      setHasMoreExtra(playedMatches.length === PAGE_SIZE)
      const next = filterByListMode(playedMatches.map((m) => toPlayedMatch(m)), listMode, trustServerFilter)
      next.sort((a, b) => sortByDate(a, b, listMode))
      setExtraGames((prev) => {
        const byId = new Map(prev.map((g) => [g._id, g]))
        next.forEach((n) => byId.set(n._id, n))
        return filterByListMode(Array.from(byId.values()), listMode, trustServerFilter).sort((a, b) => sortByDate(a, b, listMode))
      })
    } finally {
      setLoadingMore(false)
    }
  }, [roomId, totalLoaded, playedOnly, futureOnly, listMode, trustServerFilter, isDemo])

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) onClose()
  }

  if (!isOpen) return null

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-2 sm:p-4"
      onClick={handleBackdropClick}
    >
      <div
        className="w-full max-w-2xl h-[80dvh] max-h-[80dvh] sm:h-[80vh] sm:max-h-[80vh] overflow-hidden flex flex-col min-h-0 glass-strong rounded-xl sm:rounded-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-3 sm:p-5 border-b border-white/10 bg-white/5 shrink-0">
          <h2 className="text-lg sm:text-xl font-bold text-foreground flex items-center gap-2">
            <span className="flex items-center justify-center w-9 h-9 rounded-lg bg-primary/20 text-primary">
              <Gamepad2 className="w-5 h-5" />
            </span>
            {t(titleKey)}
          </h2>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-white/10 text-muted-foreground hover:text-foreground transition-colors"
            aria-label={t('stats.close')}
          >
            <span className="text-xl leading-none">×</span>
          </button>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain p-3 sm:p-5">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-10 h-10 animate-spin text-primary" />
            </div>
          ) : isError ? (
            <div className="text-center py-12 space-y-3">
              <p className="text-muted-foreground text-sm sm:text-base">{t('roomPage.playedGamesLoadFailed')}</p>
              <button
                type="button"
                onClick={() => refetch()}
                disabled={isFetching}
                className="px-4 py-2 rounded-lg bg-primary/15 text-primary font-medium hover:bg-primary/25 disabled:opacity-50"
              >
                {t('common.retry')}
              </button>
            </div>
          ) : games.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground text-sm sm:text-base">
              {t('roomPage.noPlayedGames')}
            </div>
          ) : (
            <>
              <ul className="space-y-3">
                {games.map((match) => (
                  <li key={match._id}>
                    <button
                      type="button"
                      onClick={() => onSelectGame(match)}
                      className="w-full text-left rounded-xl sm:rounded-2xl p-4 sm:p-5 transition-all border bg-card hover:bg-muted/50 border-border/50 hover:border-primary/30 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-primary/40"
                    >
                      <div className="font-bold text-foreground text-base sm:text-lg tracking-tight">
                        <MatchTeamsInline
                          teamA={match.teamA}
                          teamB={match.teamB}
                          league={match.league}
                          nameClassName="font-bold text-foreground text-base sm:text-lg tracking-tight"
                        />
                      </div>
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2 text-sm text-muted-foreground">
                        <span>{formatDate(getMatchDate(match), true)}</span>
                        {!isMatchStarted(match) ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-md bg-primary/15 text-primary font-semibold">
                            {t('stats.predicted')}
                          </span>
                        ) : footballDisplayScore(match) ? (
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-md font-semibold ${isFootballMatchLive(match) ? 'bg-red-500/20 text-red-400' : 'bg-primary/15 text-primary'}`}>
                            {footballLiveBadge(match) ? `${footballLiveBadge(match)} · ` : ''}{t('roomPage.score')}: {footballDisplayScore(match)}
                          </span>
                        ) : (
                          <span className="text-muted-foreground/80">{t('roomPage.score')}: –</span>
                        )}
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
              {hasMore && (
                <div className="mt-4 flex justify-center">
                  <button
                    type="button"
                    onClick={loadMore}
                    disabled={loadingMore}
                    className="px-6 py-2.5 rounded-xl border border-border/60 bg-muted/30 text-foreground font-medium hover:bg-muted/50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    {loadingMore ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        {t('common.loading')}
                      </>
                    ) : (
                      t('roomPage.loadMoreGames')
                    )}
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        <div className="p-3 sm:p-5 border-t border-white/10 bg-white/5 shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="w-full py-2 sm:py-3 rounded-xl bg-primary text-primary-foreground font-semibold hover:opacity-90 transition-opacity"
          >
            {t('stats.close')}
          </button>
        </div>
      </div>
    </div>
  )
}

export default GamesAndPredictionsModal
